import { html } from "htm/preact";
import { useState } from "preact/hooks";
import { adminDebugReport, checkAdminKey } from "../http.js";
import { loadCachedAdminKey, saveCachedAdminKey } from "../adminKey.js";
import { navigate } from "../router.js";

/**
 * The debug area: one real, read-only request against each external service the
 * deployment depends on, reported in full.
 *
 * It exists because the public errors deliberately say nothing. A player who can't
 * search for a course sees "course search is temporarily unavailable" and should — the
 * name of a secret is no use to them and tells everyone else what to go looking for.
 * The person who can act on it holds the admin key, so the detail lives here.
 *
 * The report renders as raw JSON on purpose. It is read once, when something is wrong,
 * by the one person who can fix it, and every field in it matters — summarising it
 * would mean deciding in advance which half of an unknown failure was the important
 * half. It only has to stay legible and not widen the page.
 */
export function AdminDebug() {
  const [key, setKey] = useState(loadCachedAdminKey());
  const [authed, setAuthed] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(withKey: string) {
    setBusy(true);
    setError(null);
    try {
      const body = await adminDebugReport(withKey);
      setReport(JSON.stringify(body, null, 2));
      setAuthed(true);
      saveCachedAdminKey(withKey);
    } catch (err) {
      setError((err as Error).message);
      setAuthed(false);
    } finally {
      setBusy(false);
    }
  }

  async function unlock(e: Event) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await checkAdminKey(key);
      saveCachedAdminKey(key);
      setAuthed(true);
      await run(key);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const header = html`
    <div class="row row-between">
      <h1 class="m-0">Debug</h1>
      <button class="secondary" onClick=${() => navigate("/admin")}>Back to admin</button>
    </div>
  `;

  if (!authed && !report) {
    return html`
      ${header}
      <form onSubmit=${unlock} class="panel">
        <label for="debug-key">Admin key</label>
        <input
          id="debug-key"
          type="password"
          value=${key}
          onInput=${(e: Event) => setKey((e.target as HTMLInputElement).value)}
          autocomplete="current-password"
        />
        <button type="submit" disabled=${busy || !key}>Unlock</button>
      </form>
      ${error && html`<p class="notice notice-error">${error}</p>`}
    `;
  }

  return html`
    ${header}
    <div class="panel">
      <p class="field-help">
        Runs one read-only request against each external service this deployment uses
        and reports exactly what came back — including whether each secret is reaching
        the service it's for. No secret value is ever shown, and nothing here writes.
      </p>
      <button class="secondary" disabled=${busy} onClick=${() => run(key)}>
        ${busy ? "Checking…" : "Run checks"}
      </button>
    </div>
    ${error && html`<p class="notice notice-error">${error}</p>`}
    ${report && html`<pre class="diagnostic-report">${report}</pre>`}
  `;
}
